import {
  Button,
  Container,
  ScrollView,
  SizableText,
  Stack,
  XStack,
  YStack,
  isWeb,
  shadowStandard,
} from '@my/ui'
import { Share2 } from '@tamagui/lucide-icons'
import { MainLayout, Meta, NativePageNavigation, StructuredMeta } from 'app/components'
import config from 'app/config'
import { ArticleBlockRenderer } from 'app/features/blog/blocks'
import { ShareModal } from 'app/features/sharing/components'
import { FreshersPageQueryOutput } from './queries'
import { FreshersNav } from '../../components'
import { ArticleCategoryTag } from 'app/features/blog/components'
import { SolitoImage } from 'solito/image'
import { RefreshControl } from 'react-native'

export interface FreshersScreenProps {
  article: FreshersPageQueryOutput['FreshersPage']

  refreshing: boolean
  onPullToRefresh: () => void
}

export const FreshersScreen = (props: FreshersScreenProps) => {
  const { article } = props
  const shareLinkUrl = config.siteUrl + '/freshers'

  return (
    <MainLayout>
      <Meta
        title={article.title}
        description={article.excerpt}
        image={article?.featuredImage?.url}
      />
      <StructuredMeta
        type="article"
        title={article.title}
        description={article.excerpt || ''}
        image={article?.featuredImage?.url}
        date={article.updatedAt}
        url={`${config.siteUrl}/freshers`}
      />
      <StructuredMeta
        type="breadcrumb"
        breadcrumbs={[
          {
            name: 'Home',
            url: config.siteUrl,
          },
          {
            name: 'Freshers',
            url: `${config.siteUrl}/freshers`,
          },
        ]}
      />
      <NativePageNavigation
        title={'Freshers Page'}
        Controls={[
          <ShareModal
            key="share"
            href={shareLinkUrl}
            trigger={<Button size="$3" px="$2" plain icon={<Share2 size={24} />} />}
          />,
        ]}
      />
      {isWeb && (
        <FreshersNav $gtSm={{ display: 'none' }} spacerProps={{ $gtSm: { display: 'none' } }} />
      )}
      <ScrollView
        refreshControl={
          <RefreshControl refreshing={props.refreshing} onRefresh={props.onPullToRefresh} />
        }
      >
        <Container size="base" gutter="gtSm" spaceBottom>
          <YStack
            flex={1}
            height="100%"
            backgroundColor="$white"
            space={4}
            justifyContent="center"
            alignItems="center"
            {...shadowStandard}
          >
            {article.featuredImage?.url && (
              <Stack w="100%" aspectRatio={39 / 20} $gtSm={{ aspectRatio: 5 / 2 }}>
                <SolitoImage
                  src={article.featuredImage.url}
                  alt={article.title}
                  fill
                  contentFit="cover"
                  priority
                  sizes="(max-width: 900px) 100vw, 900px"
                />
              </Stack>
            )}

            <YStack width="100%" flex={1} space={4} m="$4" justifyContent="space-between">
              <Container gutter>
                <XStack gap="$3.5" justifyContent="space-between">
                  <SizableText size="$9" fow="600">
                    {article.title}
                  </SizableText>
                  {isWeb && shareLinkUrl && (
                    <ShareModal
                      href={shareLinkUrl}
                      trigger={
                        <Button
                          size="$3"
                          px="$2"
                          plain
                          icon={<Share2 size={24} />}
                          fontWeight="500"
                        >
                          Share
                        </Button>
                      }
                    />
                  )}
                </XStack>

                {article.excerpt && (
                  <SizableText width="auto" my="$4" color="$grey7" size="$6">
                    {article.excerpt}
                  </SizableText>
                )}

                {article.primaryCategory?.name && (
                  <ArticleCategoryTag
                    name={article.primaryCategory.name}
                    href={`/life/categories/${article.primaryCategory.slug}`}
                    alignSelf="flex-start"
                    mb="$4"
                  />
                )}
              </Container>

              <ArticleBlockRenderer content={article.content} />
            </YStack>
          </YStack>
        </Container>
      </ScrollView>
    </MainLayout>
  )
}
